import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { 
  DollarSign, TrendingUp, CreditCard, Download,
  Clock, RefreshCw, Users, Receipt, Percent, Gift,
  Plus, Search, X, ArrowUpDown, ChevronDown, ChevronUp,
  Eye, Zap, AlertTriangle, CheckCircle2, XCircle, Hourglass, ShieldCheck, ArrowRightLeft, Loader2,
  ExternalLink, Package, Landmark, FileText, Wallet, Info
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { Switch } from "../../components/ui/switch";
import { RevenueTopCards, InvestorCards } from "./revenue/OverviewCards";
import { PlanTypeBreakdown, MrrBreakdownCard, NeedsReviewCard, MonthlyCharts } from "./revenue/RevenueCharts";

const AdminRevenue = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalTxnCount, setTotalTxnCount] = useState(0);
  const [coupons, setCoupons] = useState([]);
  const [dateRange, setDateRange] = useState("365");
  const [includeInternal, setIncludeInternal] = useState(false);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: "",
    discount_percent: 10,
    max_uses: 100,
    expires_days: 30
  });
  const [subHealth, setSubHealth] = useState(null);
  const [subHealthLoading, setSubHealthLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, includeInternal]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const inc = includeInternal ? "&include_internal=true" : "";
      const [statsRes, transRes, couponsRes] = await Promise.all([
        axios.get(`${API}/admin/revenue/stats?days=${dateRange}${inc}`, getAuthConfig()).catch(e => ({ data: null, error: e })),
        axios.get(`${API}/admin/revenue/transactions?days=${dateRange}&limit=200${inc}`, getAuthConfig()).catch(e => ({ data: { transactions: [], total_count: 0 }, error: e })),
        axios.get(`${API}/admin/coupons`, getAuthConfig()).catch(e => ({ data: { coupons: [] }, error: e }))
      ]);
      
      if (statsRes.data) setStats(statsRes.data);
      setTransactions(transRes.data?.transactions || []);
      setTotalTxnCount(transRes.data?.total_count || 0);
      setCoupons(couponsRes.data?.coupons || []);
      
      if (statsRes.error && transRes.error && couponsRes.error) {
        if (statsRes.error.response?.status !== 401) {
          toast.error("Failed to load revenue data");
        }
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async () => {
    try {
      await axios.post(`${API}/admin/coupons`, couponForm, getAuthConfig());
      toast.success("Coupon created");
      setShowCouponDialog(false);
      setCouponForm({ code: "", discount_percent: 10, max_uses: 100, expires_days: 30 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coupon");
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!confirm("Delete this coupon?")) return;
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`, getAuthConfig());
      toast.success("Coupon deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  const fetchSubHealth = async () => {
    setSubHealthLoading(true);
    try {
      const res = await axios.get(`${API}/admin/subscription-status`, getAuthConfig());
      setSubHealth(res.data);
    } catch (error) {
      toast.error("Failed to load subscription health");
    } finally {
      setSubHealthLoading(false);
    }
  };

  const runMigration = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const res = await axios.post(`${API}/admin/migrate-to-subscriptions`, {}, getAuthConfig());
      setMigrationResult(res.data);
      toast.success(res.data.message || "Migration complete");
      fetchSubHealth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await axios.post(`${API}/admin/backfill-expiration`, {}, getAuthConfig());
      setBackfillResult(res.data);
      toast.success(res.data.message || "Backfill complete");
      fetchSubHealth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const filteredTransactions = useMemo(() => {
    let result = transactions.filter(t => {
      const matchSearch = !searchTerm ||
        (t.user_email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.plan_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.transaction_id || "").toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = filterStatus === "all" || (t.effective_status || t.payment_status) === filterStatus;
      const matchType = filterType === "all" || t.payment_type === filterType;
      return matchSearch && matchStatus && matchType;
    });

    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === "amount") {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }
      if (sortField === "created_at") {
        aVal = aVal || "";
        bVal = bVal || "";
      }
      if (aVal < bVal) return sortDir === "desc" ? 1 : -1;
      if (aVal > bVal) return sortDir === "desc" ? -1 : 1;
      return 0;
    });

    return result;
  }, [transactions, searchTerm, filterStatus, filterType, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === "desc" ? "asc" : "desc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const exportCSV = () => {
    const headers = ["Date", "Transaction ID", "Payment Intent ID", "User", "Plan", "Type", "Amount", "Status", "Effective Status", "Billing Period", "Promo", "Fieldz Fee", "Internal Account", "Counts Toward Revenue", "Counts Toward Volume"];
    const esc = (v) => `"${String(v ?? "-").replace(/"/g, '""')}"`;
    const rows = filteredTransactions.map(t => [
      t.created_at ? format(new Date(t.created_at), "yyyy-MM-dd h:mm a") : "-",
      t.transaction_id || "-",
      t.stripe_payment_intent_id || "-",
      t.user_email || "-",
      t.plan_name || "-",
      t.payment_type || "-",
      (t.amount || 0).toFixed(2),
      t.payment_status || "-",
      t.effective_status || t.payment_status || "-",
      t.billing_period || "-",
      typeof t.promo_applied === "object" ? (t.promo_applied?.code || "-") : (t.promo_applied || "-"),
      t.fieldz_fee != null ? t.fieldz_fee.toFixed(2) : "-",
      t.is_internal ? "yes" : "no",
      t.counts_toward_revenue ? "yes" : "no",
      t.counts_toward_volume ? "yes" : "no",
    ].map(esc));
    const csv = [headers.map(esc), ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  const getStatusBadge = (status) => {
    const map = {
      paid: { class: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", icon: CheckCircle2, label: "Paid" },
      pending: { class: "bg-amber-500/15 text-amber-400 border-amber-500/30", icon: Hourglass, label: "Pending" },
      unpaid: { class: "bg-red-500/15 text-red-400 border-red-500/30", icon: XCircle, label: "Unpaid" },
      failed: { class: "bg-red-500/15 text-red-400 border-red-500/30", icon: XCircle, label: "Failed" },
      expired: { class: "bg-slate-500/15 text-slate-400 border-slate-500/30", icon: Clock, label: "Expired" },
      refunded: { class: "bg-purple-500/15 text-purple-400 border-purple-500/30", icon: Receipt, label: "Refunded" },
    };
    const s = map[status] || { class: "bg-slate-500/15 text-slate-400 border-slate-500/30", label: status || "Unknown" };
    return (
      <Badge className={`text-[10px] px-2 py-0.5 border font-medium ${s.class}`}>
        {s.icon && <s.icon className="w-3 h-3 mr-1 inline" />}
        {s.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const map = {
      subscription_upgrade: { class: "bg-blue-500/15 text-blue-400", label: "Subscription" },
      subscription_renewal: { class: "bg-blue-500/15 text-blue-400", label: "Renewal" },
      account_topup: { class: "bg-emerald-500/15 text-emerald-400", label: "Top-up" },
      usage_topup: { class: "bg-emerald-500/15 text-emerald-400", label: "Usage Top-up" },
      connect_invoice_payment: { class: "bg-amber-500/15 text-amber-400", label: "Shop Payment" },
      portal_invoice_payment: { class: "bg-amber-500/15 text-amber-400", label: "Shop Payment (Portal)" },
      tech_onsite_payment: { class: "bg-amber-500/15 text-amber-400", label: "Shop Payment (On-site)" },
      invoice_payment: { class: "bg-cyan-500/15 text-cyan-400", label: "Invoice (legacy)" },
      customer_invoice_payment: { class: "bg-cyan-500/15 text-cyan-400", label: "Invoice (legacy)" },
    };
    const s = map[type] || { class: "bg-slate-500/15 text-slate-400", label: type || "Unknown" };
    return <Badge className={`text-[10px] px-1.5 py-0 font-normal ${s.class}`}>{s.label}</Badge>;
  };

  const SortHeader = ({ label, field, className = "" }) => (
    <th
      className={`text-left text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1.5">
        {label}
        {sortField === field ? (
          sortDir === "desc" ? <ChevronDown className="w-3.5 h-3.5 text-blue-400" /> : <ChevronUp className="w-3.5 h-3.5 text-blue-400" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-40" />
        )}
      </div>
    </th>
  );

  return (
    <div className="space-y-6" data-testid="admin-revenue-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']" data-testid="revenue-page-title">Revenue & Billing</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Financial overview from real payment data
            {stats && <span className="ml-1 text-xs">({stats.period_days}d window)</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground mr-1 cursor-pointer select-none" data-testid="include-internal-toggle-label">
            <Switch checked={includeInternal} onCheckedChange={setIncludeInternal} data-testid="include-internal-toggle" />
            Include internal accounts
          </label>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px] bg-card border-border text-foreground h-9 text-sm" data-testid="date-range-select">
              <Clock className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} className="border-border text-foreground h-9" data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button size="sm" onClick={exportCSV} className="bg-[#0046BE] hover:bg-[#003da6] text-white h-9" data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="border-border animate-pulse">
              <CardContent className="p-5"><div className="h-16 bg-muted rounded" /></CardContent>
            </Card>
          ))}
        </div>
      ) : stats && (
        <>
          <RevenueTopCards stats={stats} />
          <InvestorCards stats={stats} />
          <PlanTypeBreakdown stats={stats} />
          <MonthlyCharts stats={stats} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <MrrBreakdownCard stats={stats} />
            <div className="space-y-4">
              <NeedsReviewCard stats={stats} />
              {stats.trial_to_paid?.note && (
                <p className="text-[11px] text-muted-foreground px-1" data-testid="trial-note">ⓘ {stats.trial_to_paid.note}</p>
              )}
              {!includeInternal && (
                <p className="text-[11px] text-muted-foreground px-1" data-testid="internal-excluded-note">
                  ⓘ {stats.internal_account_count || 0} internal account(s) excluded from all figures. Use the toggle above to include them.
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Tabs */}
      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList className="bg-card border border-border h-10 max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="transactions" className="data-[state=active]:bg-muted text-foreground text-sm" data-testid="transactions-tab">
            <Receipt className="w-3.5 h-3.5 mr-1.5" /> Transactions
            <Badge className="ml-2 text-[10px] bg-muted/80 text-muted-foreground">{totalTxnCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="coupons" className="data-[state=active]:bg-muted text-foreground text-sm" data-testid="coupons-tab">
            <Gift className="w-3.5 h-3.5 mr-1.5" /> Coupons
            <Badge className="ml-2 text-[10px] bg-muted/80 text-muted-foreground">{coupons.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="data-[state=active]:bg-muted text-foreground text-sm" data-testid="subscriptions-tab" onClick={() => { if (!subHealth) fetchSubHealth(); }}>
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Subscriptions
          </TabsTrigger>
        </TabsList>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card className="border-border bg-card" data-testid="transactions-card">
            <CardHeader className="pb-3">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-foreground text-base font-['Manrope']">Payment Transactions</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {filteredTransactions.length} of {transactions.length} transactions
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search email, plan, ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-background border-border text-foreground pl-8 pr-8 h-9 text-sm w-52"
                      data-testid="search-transactions-input"
                    />
                    {searchTerm && (
                      <button onClick={() => setSearchTerm("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[120px] bg-background border-border text-foreground h-9 text-sm" data-testid="filter-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="unpaid">Unpaid</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[140px] bg-background border-border text-foreground h-9 text-sm" data-testid="filter-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="subscription_upgrade">Subscription</SelectItem>
                      <SelectItem value="subscription_renewal">Renewal</SelectItem>
                      <SelectItem value="account_topup">Top-up</SelectItem>
                      <SelectItem value="connect_invoice_payment">Shop Payment (Connect)</SelectItem>
                      <SelectItem value="portal_invoice_payment">Shop Payment (Portal)</SelectItem>
                      <SelectItem value="tech_onsite_payment">Shop Payment (On-site)</SelectItem>
                      <SelectItem value="invoice_payment">Invoice (legacy)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="transactions-table">
                  <thead>
                    <tr className="border-y border-border bg-muted/30">
                      <SortHeader label="Date" field="created_at" />
                      <th className="text-left text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground">User</th>
                      <th className="text-left text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground">Plan / Type</th>
                      <SortHeader label="Amount" field="amount" />
                      <th className="text-left text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground">Status</th>
                      <th className="text-left text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground">Revenue</th>
                      <th className="text-center text-xs font-semibold uppercase tracking-wider py-3.5 px-4 text-muted-foreground w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((t) => (
                      <tr
                        key={t.transaction_id || t.session_id}
                        className="border-b border-border/50 hover:bg-muted/30 transition-colors group cursor-pointer"
                        data-testid={`txn-row-${t.transaction_id}`}
                        onClick={() => setSelectedTxn(t)}
                      >
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm text-foreground">
                              {t.created_at ? format(new Date(t.created_at), "MMM dd, yyyy") : "-"}
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {t.created_at ? format(new Date(t.created_at), "h:mm a") : ""}
                            </p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-foreground truncate max-w-[200px]">{t.user_email || "-"}</p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-sm text-foreground font-medium">{t.plan_name || "-"}</span>
                            {getTypeBadge(t.payment_type)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm font-semibold text-foreground tabular-nums">
                            ${(t.amount || 0).toFixed(2)}
                          </span>
                          {t.billing_period && (
                            <span className="text-[10px] text-muted-foreground ml-1">/{t.billing_period === "monthly" ? "mo" : t.billing_period === "yearly" ? "yr" : t.billing_period}</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(t.effective_status || t.payment_status)}
                          {t.is_internal && <Badge className="ml-1 text-[10px] bg-amber-500/15 text-amber-400 border border-amber-500/30">Internal</Badge>}
                        </td>
                        <td className="py-3 px-4">
                          {t.counts_toward_revenue ? (
                            <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Fieldz revenue</Badge>
                          ) : t.counts_toward_volume ? (
                            <div className="flex flex-col gap-0.5">
                              <Badge className="text-[10px] bg-amber-500/15 text-amber-400 border border-amber-500/30 w-fit">Volume only</Badge>
                              {t.fieldz_fee != null && <span className="text-[10px] text-cyan-400">fee ${t.fieldz_fee.toFixed(2)}</span>}
                            </div>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); setSelectedTxn(t); }}
                            data-testid={`view-txn-${t.transaction_id}`}
                          >
                            <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredTransactions.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="no-transactions">
                    <Receipt className="w-10 h-10 text-muted-foreground/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">No transactions found</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Try adjusting your filters or date range</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coupons Tab */}
        <TabsContent value="coupons">
          <Card className="border-border bg-card" data-testid="coupons-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-foreground text-base font-['Manrope']">Discount Coupons</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">{coupons.length} coupons</p>
                </div>
                <Button size="sm" onClick={() => setShowCouponDialog(true)} className="bg-[#0046BE] hover:bg-[#003da6] text-white h-9" data-testid="create-coupon-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Create Coupon
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {coupons.map((c) => (
                  <div key={c.coupon_id} className="p-4 rounded-lg border border-border bg-background flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                        <Percent className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground font-mono">{c.code}</p>
                        <p className="text-xs text-muted-foreground">
                          {c.discount_percent}% off
                          <span className="mx-1.5">·</span>
                          {c.uses || 0}/{c.max_uses} uses
                          {c.expires_at && (
                            <>
                              <span className="mx-1.5">·</span>
                              Expires {format(new Date(c.expires_at), "MMM dd, yyyy")}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] border ${c.is_active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-slate-500/15 text-slate-400 border-slate-500/30"}`}>
                        {c.is_active ? "Active" : "Inactive"}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => deleteCoupon(c.coupon_id)} className="text-red-400 hover:text-red-300 h-8 px-2 text-xs" data-testid={`delete-coupon-${c.coupon_id}`}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
                {coupons.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Gift className="w-10 h-10 text-muted-foreground/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">No coupons yet</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Create one to offer discounts</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          <div className="space-y-4">
            {/* Health Check Card */}
            <Card className="border-border bg-card" data-testid="subscription-health-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-foreground text-base font-['Manrope']">Subscription Health</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">Overview of all user subscriptions and Stripe sync status</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={fetchSubHealth} disabled={subHealthLoading} className="border-border text-foreground h-9" data-testid="refresh-sub-health-btn">
                    {subHealthLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {subHealthLoading && !subHealth ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : subHealth ? (
                  <div className="space-y-4">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="p-4 rounded-lg border border-border bg-background">
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="w-4 h-4 text-blue-400" />
                          <span className="text-xs text-muted-foreground">Total Paid Users</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground tabular-nums" data-testid="total-paid-users">{subHealth.total_paid_users}</p>
                      </div>
                      <div className="p-4 rounded-lg border border-border bg-background">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          <span className="text-xs text-muted-foreground">With Stripe Sub</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground tabular-nums" data-testid="users-with-stripe">{subHealth.users_with_stripe_subscription}</p>
                      </div>
                      <div className="p-4 rounded-lg border border-border bg-background">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4 text-amber-400" />
                          <span className="text-xs text-muted-foreground">Missing Stripe Sub</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground tabular-nums" data-testid="users-missing-stripe">{subHealth.users_missing_stripe_subscription}</p>
                      </div>
                      <div className="p-4 rounded-lg border border-border bg-background">
                        <div className="flex items-center gap-2 mb-1">
                          <ArrowRightLeft className="w-4 h-4 text-purple-400" />
                          <span className="text-xs text-muted-foreground">One-Time Payments</span>
                        </div>
                        <p className="text-2xl font-bold text-foreground tabular-nums" data-testid="one-time-payments">{subHealth.one_time_payment_transactions}</p>
                      </div>
                    </div>

                    {/* Migration Alert */}
                    {subHealth.needs_migration && (
                      <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/10 flex items-start gap-3" data-testid="migration-alert">
                        <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">Migration Needed</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {subHealth.one_time_payment_transactions} user(s) paid via one-time payment instead of subscription. 
                            Run migration to create proper Stripe subscriptions for them.
                          </p>
                          <Button
                            size="sm"
                            onClick={runMigration}
                            disabled={migrating}
                            className="mt-3 bg-amber-500 hover:bg-amber-600 text-black h-8 text-xs"
                            data-testid="run-migration-btn"
                          >
                            {migrating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />}
                            {migrating ? "Migrating..." : "Migrate to Subscriptions"}
                          </Button>
                        </div>
                      </div>
                    )}

                    {!subHealth.needs_migration && (
                      <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 flex items-center gap-3" data-testid="all-good-alert">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-foreground">All Clear</p>
                          <p className="text-xs text-muted-foreground">No one-time payments found. All checkout sessions use subscription mode.</p>
                        </div>
                      </div>
                    )}

                    {/* Backfill Expiration Dates */}
                    {subHealth.users_missing_stripe_subscription > 0 && (
                      <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/10 flex items-start gap-3" data-testid="backfill-alert">
                        <Clock className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">Set Expiration Dates</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {subHealth.users_missing_stripe_subscription} user(s) on paid plans without a Stripe subscription.
                            Set expiration dates so they'll be prompted to subscribe when their paid period ends.
                          </p>
                          <Button
                            size="sm"
                            onClick={runBackfill}
                            disabled={backfilling}
                            className="mt-3 bg-blue-500 hover:bg-blue-600 text-white h-8 text-xs"
                            data-testid="run-backfill-btn"
                          >
                            {backfilling ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Clock className="w-3.5 h-3.5 mr-1.5" />}
                            {backfilling ? "Setting..." : "Set Expiration Dates"}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Backfill Result */}
                    {backfillResult && (
                      <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/10" data-testid="backfill-result">
                        <p className="text-sm font-medium text-foreground mb-2">Backfill Result</p>
                        <p className="text-xs text-muted-foreground">{backfillResult.message}</p>
                        {backfillResult.results?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {backfillResult.results.map((r, i) => (
                              <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-background/50 border border-border gap-2">
                                <span className="text-muted-foreground truncate">{r.email}</span>
                                <div className="flex items-center gap-2 shrink-0">
                                  {r.expires_at && <span className="text-blue-400 text-[10px]">expires {new Date(r.expires_at).toLocaleDateString()}</span>}
                                  {r.error && <span className="text-red-400 text-[10px] max-w-[200px] truncate" title={r.error}>{r.error}</span>}
                                  <Badge className={`text-[10px] ${r.status === "updated" ? "bg-emerald-500/15 text-emerald-400" : r.status === "failed" ? "bg-red-500/15 text-red-400" : "bg-slate-500/15 text-slate-400"}`}>
                                    {r.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Migration Result */}
                    {migrationResult && (
                      <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/10" data-testid="migration-result">
                        <p className="text-sm font-medium text-foreground mb-2">Migration Result</p>
                        <p className="text-xs text-muted-foreground">{migrationResult.message}</p>
                        {migrationResult.results?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {migrationResult.results.map((r, i) => (
                              <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-background/50 border border-border gap-2">
                                <span className="text-muted-foreground truncate">{r.email}</span>
                                <div className="flex items-center gap-2 shrink-0">
                                  {r.error && <span className="text-red-400 text-[10px] max-w-[300px] truncate" title={r.error}>{r.error}</span>}
                                  <Badge className={`text-[10px] ${r.status === "migrated" ? "bg-emerald-500/15 text-emerald-400" : r.status === "failed" ? "bg-red-500/15 text-red-400" : "bg-slate-500/15 text-slate-400"}`}>
                                    {r.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Paid Users Table */}
                    {subHealth.paid_users?.length > 0 && (
                      <Card className="border-border bg-background">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-foreground text-sm font-['Manrope']">Paid Users ({subHealth.paid_users.length})</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <table className="w-full" data-testid="paid-users-table">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="text-left text-xs font-medium text-muted-foreground py-2 pr-4">Email</th>
                                  <th className="text-left text-xs font-medium text-muted-foreground py-2 pr-4">Plan</th>
                                  <th className="text-left text-xs font-medium text-muted-foreground py-2 pr-4">Status</th>
                                  <th className="text-left text-xs font-medium text-muted-foreground py-2 pr-4">Billing</th>
                                  <th className="text-left text-xs font-medium text-muted-foreground py-2 pr-4">Expires</th>
                                  <th className="text-left text-xs font-medium text-muted-foreground py-2">Stripe Sub</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subHealth.paid_users.map((u, i) => (
                                  <tr key={i} className="border-b border-border/50 last:border-0">
                                    <td className="py-2.5 pr-4 text-sm text-foreground">{u.email}</td>
                                    <td className="py-2.5 pr-4">
                                      <Badge className="text-[10px] bg-blue-500/15 text-blue-400 capitalize">{u.subscription_plan}</Badge>
                                    </td>
                                    <td className="py-2.5 pr-4">
                                      <Badge className={`text-[10px] ${u.subscription_status === "active" ? "bg-emerald-500/15 text-emerald-400" : u.subscription_status === "expired" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>
                                        {u.subscription_status}
                                      </Badge>
                                    </td>
                                    <td className="py-2.5 pr-4 text-xs text-muted-foreground capitalize">{u.billing_period || "—"}</td>
                                    <td className="py-2.5 pr-4 text-xs text-muted-foreground">
                                      {u.subscription_expires_at ? new Date(u.subscription_expires_at).toLocaleDateString() : u.stripe_subscription_id ? "Auto-renews" : "—"}
                                    </td>
                                    <td className="py-2.5 text-xs">
                                      {u.stripe_subscription_id ? (
                                        <span className="text-emerald-400 font-mono text-[10px]">{u.stripe_subscription_id.slice(0, 20)}...</span>
                                      ) : (
                                        <span className="text-amber-400">Not linked</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <ShieldCheck className="w-10 h-10 text-muted-foreground/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">Click Refresh to load subscription health</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Transaction Detail Modal */}
      <TxnDetailModal txn={selectedTxn} onClose={() => setSelectedTxn(null)} getStatusBadge={getStatusBadge} getTypeBadge={getTypeBadge} />

      {/* Create Coupon Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="bg-card border-border" aria-describedby="coupon-dialog-desc">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope']">Create Coupon</DialogTitle>
            <p id="coupon-dialog-desc" className="sr-only">Create a new discount coupon</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Coupon Code</label>
              <Input
                value={couponForm.code}
                onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                placeholder="SAVE20"
                className="bg-background border-border text-foreground mt-1"
                data-testid="coupon-code-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Discount %</label>
                <Input
                  type="number"
                  value={couponForm.discount_percent}
                  onChange={(e) => setCouponForm({...couponForm, discount_percent: parseInt(e.target.value) || 0})}
                  className="bg-background border-border text-foreground mt-1"
                  data-testid="coupon-discount-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Max Uses</label>
                <Input
                  type="number"
                  value={couponForm.max_uses}
                  onChange={(e) => setCouponForm({...couponForm, max_uses: parseInt(e.target.value) || 0})}
                  className="bg-background border-border text-foreground mt-1"
                  data-testid="coupon-max-uses-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Expires (days)</label>
                <Input
                  type="number"
                  value={couponForm.expires_days}
                  onChange={(e) => setCouponForm({...couponForm, expires_days: parseInt(e.target.value) || 0})}
                  className="bg-background border-border text-foreground mt-1"
                  data-testid="coupon-expires-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCouponDialog(false)} className="text-foreground">Cancel</Button>
            <Button onClick={createCoupon} className="bg-[#0046BE] hover:bg-[#003da6] text-white" data-testid="create-coupon-submit-btn">Create Coupon</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TxnDetailModal = ({ txn, onClose, getStatusBadge, getTypeBadge }) => {
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  useEffect(() => {
    if (!txn?.transaction_id) return;
    let on = true;
    setDetails(null); setDetailsError(null); setLoadingDetails(true);
    axios.get(`${API}/admin/revenue/transactions/${txn.transaction_id}/details`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => { if (on) setDetails(res.data); })
      .catch(() => { if (on) setDetailsError("Couldn't load full Stripe details — showing stored record only."); })
      .finally(() => { if (on) setLoadingDetails(false); });
    return () => { on = false; };
  }, [txn?.transaction_id]);

  if (!txn) return null;

  const cur = (details?.overview?.currency || txn.currency || "usd").toUpperCase();
  const money = (v) => (v === null || v === undefined ? "Not available" : `$${Number(v).toFixed(2)} ${cur}`);
  const ts = (unix) => (unix ? format(new Date(unix * 1000), "MMM dd, yyyy h:mm a") : null);
  const o = details?.overview;
  const rev = details?.revenue;
  const bd = details?.breakdown;
  const subs = details?.subscription;
  const refs = details?.stripe_refs;

  const Section = ({ icon: Icon, title, children, testId }) => (
    <div className="rounded-xl border border-border bg-muted/20 overflow-hidden" data-testid={testId}>
      <div className="px-3 py-2 border-b border-border/60 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );

  const Row = ({ label, value, mono, strong, color }) => (
    <div className="flex justify-between items-start gap-4 py-0.5 text-sm">
      <span className="text-muted-foreground text-xs whitespace-nowrap">{label}</span>
      <span className={`text-right break-all ${mono ? "font-mono text-[11px]" : "text-xs"} ${strong ? "font-bold text-sm" : ""} ${color || "text-foreground"}`}>
        {value === null || value === undefined || value === "" ? "Not available" : value}
      </span>
    </div>
  );

  return (
    <Dialog open={!!txn} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="txn-detail-modal" aria-describedby="txn-detail-desc">
        <DialogHeader>
          <DialogTitle className="text-foreground font-['Manrope'] text-lg">Transaction Details</DialogTitle>
          <p id="txn-detail-desc" className="sr-only">Detailed view of a payment transaction</p>
        </DialogHeader>

        {/* Header: status + amounts highlight */}
        <div className="flex items-center gap-2 flex-wrap pb-3 border-b border-border">
          {getStatusBadge(txn.payment_status)}
          {getTypeBadge(txn.payment_type)}
          {o?.is_connect && <Badge className="bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 text-[10px]">Stripe Connect</Badge>}
          <div className="ml-auto flex items-center gap-4">
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground uppercase">Charged</p>
              <p className="text-lg font-bold text-foreground tabular-nums" data-testid="txn-amount-charged">${(Number(txn.amount) || 0).toFixed(2)}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground uppercase">Fieldz AI earned</p>
              <p className="text-lg font-bold text-emerald-400 tabular-nums" data-testid="txn-fieldz-earned">
                {rev?.fieldz_earned !== null && rev?.fieldz_earned !== undefined ? `$${Number(rev.fieldz_earned).toFixed(2)}` : (loadingDetails ? "…" : "N/A")}
              </p>
            </div>
          </div>
        </div>

        {loadingDetails && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="txn-details-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading full Stripe details…
          </div>
        )}
        {detailsError && (
          <div className="text-xs text-amber-500 flex items-center gap-1.5 py-1"><AlertTriangle className="w-3.5 h-3.5" /> {detailsError}</div>
        )}

        <div className="space-y-3">
          {/* 1. Overview */}
          <Section icon={Receipt} title="Overview" testId="txn-sec-overview">
            <Row label="Transaction ID" value={txn.transaction_id} mono />
            <Row label="Customer" value={o?.customer_name || txn.customer_name} />
            <Row label="Email" value={txn.user_email} />
            <Row label="Date" value={txn.created_at ? format(new Date(txn.created_at), "MMM dd, yyyy h:mm:ss a") : null} />
            {o?.paid_at && <Row label="Paid at" value={ts(o.paid_at)} />}
            <Row label="Type" value={txn.payment_type} />
            <Row label="Payment method" value={o?.payment_method ? `${o.payment_method.brand?.toUpperCase()} •••• ${o.payment_method.last4}` : (loadingDetails ? "…" : "Not available")} />
            <Row label="Stripe customer" value={o?.stripe_customer_id || txn.stripe_customer_id} mono />
            {(o?.billing_period_start || o?.billing_period_end) && (
              <Row label="Billing period" value={`${ts(o.billing_period_start) || "?"} → ${ts(o.billing_period_end) || "?"}`} />
            )}
            {o?.connected_account_id && <Row label="Connected account" value={o.connected_account_id} mono />}
          </Section>

          {/* 2. Purchased Items */}
          <Section icon={Package} title="Purchased Items" testId="txn-sec-items">
            {details?.items?.length ? (
              <div className="space-y-1.5">
                {details.items.map((it, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 text-xs border-b border-border/40 last:border-0 pb-1.5 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-foreground">{it.name}{it.proration && <Badge className="ml-1.5 bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[9px] px-1">Prorated</Badge>}</p>
                      <p className="text-muted-foreground text-[10px]">
                        Qty {it.quantity}{it.unit_amount !== null && it.unit_amount !== undefined ? ` × $${Number(it.unit_amount).toFixed(2)}` : ""}
                        {it.interval ? ` · billed ${it.interval}ly` : ""}
                        {it.period_start ? ` · ${format(new Date(it.period_start * 1000), "MMM d")} – ${it.period_end ? format(new Date(it.period_end * 1000), "MMM d, yyyy") : ""}` : ""}
                      </p>
                    </div>
                    <span className={`tabular-nums font-medium ${Number(it.amount) < 0 ? "text-emerald-400" : "text-foreground"}`}>
                      {it.amount !== null && it.amount !== undefined ? `$${Number(it.amount).toFixed(2)}` : "—"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                {loadingDetails ? "Loading…" : (
                  <>
                    <div className="flex justify-between"><span className="text-foreground">{txn.billing_description || txn.plan_name || "Payment"}</span><span className="tabular-nums">${(Number(txn.amount) || 0).toFixed(2)}</span></div>
                    <p className="mt-1 text-[10px]">Stripe line items not available for this record.</p>
                  </>
                )}
              </div>
            )}
          </Section>

          {/* 3. Payment Breakdown */}
          <Section icon={FileText} title="Payment Breakdown" testId="txn-sec-breakdown">
            {bd && (bd.subtotal !== null || bd.total !== null) ? (
              <>
                <Row label="Subtotal" value={bd.subtotal !== null ? money(bd.subtotal) : null} />
                {(bd.discounts || []).map((dd, i) => (
                  <Row key={i} label={`Discount — ${dd.name}`} value={`-${money(dd.amount)}`} color="text-emerald-400" />
                ))}
                {bd.tax !== null && bd.tax !== undefined && <Row label="Tax" value={money(bd.tax)} />}
                {bd.starting_balance !== null && bd.starting_balance < 0 && (
                  <Row label="Customer credit applied" value={`-${money(Math.min(-bd.starting_balance, bd.total ?? -bd.starting_balance))}`} color="text-emerald-400" />
                )}
                <div className="border-t border-border/60 mt-1 pt-1">
                  <Row label="Amount charged" value={money(bd.amount_paid ?? bd.amount_due ?? bd.total)} strong />
                </div>
              </>
            ) : (
              <Row label="Amount charged" value={`$${(Number(txn.amount) || 0).toFixed(2)} ${cur}`} strong />
            )}
            {bd?.explanation?.length > 0 && (
              <div className="mt-2 rounded-lg bg-blue-500/10 border border-blue-500/20 p-2 space-y-0.5" data-testid="txn-explanation">
                {bd.explanation.map((e, i) => (
                  <p key={i} className="text-[11px] text-blue-300 flex items-start gap-1.5"><Info className="w-3 h-3 mt-0.5 shrink-0" />{e}</p>
                ))}
              </div>
            )}
          </Section>

          {/* 4. Fieldz AI Revenue */}
          <Section icon={Wallet} title="Fieldz AI Revenue & Fees" testId="txn-sec-revenue">
            {rev ? (
              <>
                <Row label="Gross amount" value={money(rev.gross)} />
                {rev.refunded !== null && rev.refunded > 0 && <Row label="Refunded" value={`-${money(rev.refunded)}`} color="text-red-400" />}
                <Row label="Stripe processing fee" value={rev.stripe_fee !== null ? `-${money(rev.stripe_fee)}` : "Not available"} color={rev.stripe_fee !== null ? "text-red-400" : undefined} />
                {rev.kind === "connect" && (
                  <>
                    <Row label="Platform (application) fee" value={money(rev.application_fee)} />
                    <Row label="Belongs to connected business" value={money(rev.connected_business_amount)} color="text-indigo-400" />
                  </>
                )}
                {rev.net_received !== null && <Row label="Net received (Stripe)" value={money(rev.net_received)} />}
                <div className="border-t border-border/60 mt-1 pt-1">
                  <Row label="Fieldz AI earned" value={money(rev.fieldz_earned)} strong color="text-emerald-400" />
                </div>
                {(rev.notes || []).map((n, i) => (
                  <p key={i} className="text-[10px] text-muted-foreground mt-1 flex items-start gap-1"><Info className="w-3 h-3 mt-0.5 shrink-0" />{n}</p>
                ))}
              </>
            ) : (
              <p className="text-xs text-muted-foreground">{loadingDetails ? "Loading…" : "Not available"}</p>
            )}
          </Section>

          {/* 5. Subscription Details */}
          {(txn.plan_name || subs?.current) && (
            <Section icon={RefreshCw} title="Subscription Details" testId="txn-sec-subscription">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">At time of purchase</p>
              <Row label="Plan" value={subs?.at_purchase?.plan_name || txn.plan_name} />
              <Row label="Billing" value={subs?.at_purchase?.billing_period || txn.billing_period} />
              <Row label="Monthly price" value={subs?.at_purchase?.monthly_price ? `$${Number(subs.at_purchase.monthly_price).toFixed(2)}` : null} />
              <Row label="Description" value={subs?.at_purchase?.description || txn.billing_description} />
              <Row label="Promo applied" value={subs?.at_purchase?.promo_applied || "None"} />
              {subs?.billing_reason && <Row label="Billing reason" value={subs.billing_reason.replace(/_/g, " ")} />}
              {subs?.current && (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2 mb-1">Current subscription</p>
                  <Row label="Status" value={subs.current.status} />
                  <Row label="Interval" value={subs.current.interval ? `${subs.current.interval}ly` : null} />
                  <Row label="Price" value={subs.current.price !== null ? money(subs.current.price) : null} />
                  <Row label="Current period" value={`${ts(subs.current.period_start) || "?"} → ${ts(subs.current.period_end) || "?"}`} />
                  <Row label="Next billing" value={subs.current.next_billing ? ts(subs.current.next_billing) : (subs.current.cancel_at_period_end ? "Cancels at period end" : null)} />
                </>
              )}
            </Section>
          )}

          {/* 6. Stripe & Payment Information */}
          <Section icon={Landmark} title="Stripe & Payment Information" testId="txn-sec-stripe">
            <Row label="Session ID" value={refs?.session_id || txn.session_id} mono />
            <Row label="Invoice ID" value={refs?.invoice_id} mono />
            <Row label="Invoice number" value={refs?.invoice_number || txn.invoice_number} mono />
            <Row label="Payment Intent" value={refs?.payment_intent_id} mono />
            <Row label="Charge ID" value={refs?.charge_id} mono />
            <Row label="Balance Transaction" value={refs?.balance_transaction_id} mono />
            <Row label="Subscription ID" value={refs?.subscription_id || txn.stripe_subscription_id} mono />
            {refs?.failure && (
              <div className="mt-2 rounded-lg bg-red-500/10 border border-red-500/20 p-2" data-testid="txn-failure">
                <p className="text-[11px] text-red-400">Failure: {refs.failure.message} {refs.failure.code ? `(${refs.failure.code})` : ""}</p>
              </div>
            )}
            {refs?.refunds?.length > 0 && (
              <div className="mt-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Refunds</p>
                {refs.refunds.map((r) => (
                  <Row key={r.id} label={`${r.id?.slice(0, 14)}… ${r.reason ? `(${r.reason})` : ""}`} value={`-${money(r.amount)} · ${r.status}${r.created ? ` · ${ts(r.created)}` : ""}`} color="text-red-400" />
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-3 flex-wrap">
              {refs?.receipt_url && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-border" onClick={() => window.open(refs.receipt_url, "_blank", "noopener")} data-testid="txn-receipt-link">
                  <Receipt className="w-3 h-3 mr-1" /> Receipt
                </Button>
              )}
              {refs?.dashboard_url && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-border" onClick={() => window.open(refs.dashboard_url, "_blank", "noopener")} data-testid="txn-stripe-dashboard-link">
                  <ExternalLink className="w-3 h-3 mr-1" /> View in Stripe
                </Button>
              )}
            </div>
          </Section>

          {details?.availability?.length > 0 && (
            <p className="text-[10px] text-muted-foreground flex items-start gap-1" data-testid="txn-availability-notes">
              <Info className="w-3 h-3 mt-0.5 shrink-0" /> Some Stripe data was unavailable: {details.availability.join("; ")}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminRevenue;
